import { Alert, Collapse } from "@mantine/core"
import { Button, Icon, Tooltip } from "@shopify/polaris"
import { CircleInformationMajor } from "@shopify/polaris-icons"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { LanguageSelector } from "../components/LanguageSelector.jsx"
import { OnboardingTimeline } from "../components/OnboardingTimeline.jsx"
import { Spinner } from "../components/Spinner.jsx"
import { TopMenu } from "../components/TopMenu.jsx"
import { Video } from "../components/Video.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { useSaveStore } from "../hooks/useFirestoreUserHooks.js"
import { useFetchMyshopifyDomain } from "../hooks/useFetchStoreNames.js"
import { usePostHog } from "posthog-js/react"
import { useSearchParams } from "react-router-dom"
import { useMediaQuery } from "../hooks/useMediaQuery.js"
import { useElementDimensions } from "../hooks/useElementDimensions.js"
import {
	captureMetaAdsEvent,
	META_ADS_EVENTS,
} from "../utils/posthogMetaAds.js"

const CopyShopifyStoreUrlVideo =
	"https://kopy.b-cdn.net/copy%20shopify%20store%20url%20zoomed.mp4"

const SaveStore = ({ newUser = false }) => {
	const posthog = usePostHog()
	const { state, dispatch } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const saveStore = useSaveStore()
	const fetchMyshopifyDomain = useFetchMyshopifyDomain()
	const [searchParams, setSearchParams] = useSearchParams()
	const isMobile = useMediaQuery("(max-width: 500px)")
	const { right: containerRight, ref: containerRef } = useElementDimensions()

	const [shopDomain, setShopDomain] = useState("")
	const [openedShowMore, setOpenedShowMore] = useState(false)

	const [loading, setLoading] = useState(false)
	const [errorCode, setErrorCode] = useState("")
	const [censoredEmail, setCensoredEmail] = useState("")

	const changeStore = (e) => {
		setShopDomain(e.target.value)
	}

	const _saveStore = async (shopToSave) => {
		setLoading(true)
		setErrorCode("")
		setCensoredEmail("")
		let domain = null

		if (!state.userId) {
			setLoading(false)
			setErrorCode("no_userid_field")
			return
		}

		// Clean the input by removing protocol and trailing slashes
		let cleanedShopDomain = shopToSave
			.replace(/https?:\/\//, "")
			.replace(/\/$/, "")
			.replace(/^\//, "")
			.trim()

		// Check if it's an admin.shopify.com URL format
		const adminShopifyMatch = cleanedShopDomain.match(
			/admin\.shopify\.com\/store\/([^\/\?#]+)/,
		)
		if (adminShopifyMatch) {
			// Extract store name and convert to myshopify.com format
			cleanedShopDomain = `${adminShopifyMatch[1]}.myshopify.com`
		}

		setShopDomain(cleanedShopDomain)

		const isValidMyshopifyDomain = /^[\w-]+\.myshopify\.com$/.test(
			cleanedShopDomain,
		)

		if (!isValidMyshopifyDomain) {
			// In case user enters a custom domain instead of a myshopify.com domain
			const fetchedMyshopifyDomain =
				await fetchMyshopifyDomain(cleanedShopDomain)

			if (fetchedMyshopifyDomain) {
				domain = fetchedMyshopifyDomain
			} else {
				setLoading(false)
				setErrorCode("invalid_domain")
				return
			}
		} else {
			// Even if it looks like a valid myshopify domain, verify it exists
			// by fetching the metadata
			const fetchedMyshopifyDomain =
				await fetchMyshopifyDomain(cleanedShopDomain)

			if (fetchedMyshopifyDomain) {
				domain = fetchedMyshopifyDomain
			} else {
				setLoading(false)
				setErrorCode("invalid_domain")
				return
			}
		}

		setLoading(true)
		const { success, errorCode, censoredEmail } = await saveStore(
			state.userId,
			domain,
		)
		setLoading(false)

		if (errorCode) {
			setErrorCode(errorCode)
			if (censoredEmail) {
				setCensoredEmail(censoredEmail)
			}
			return
		}

		if (success) {
			const newUser = !state.hasCompletedOnBoarding
			const hasOtherStoresLinked = Object.keys(state.stores).length > 0
			posthog?.capture(
				"user_saves_store_v2",
				{ store: domain, newUser },
				{ transport: "sendBeacon" },
			)
			if (!hasOtherStoresLinked) {
				captureMetaAdsEvent(
					posthog,
					META_ADS_EVENTS.COMPLETE_REGISTRATION,
					{ store: domain, newUser },
					{ transport: "sendBeacon" },
				)
			}
			window.location.href = "/"
		}
	}

	const handleNoStoreClick = () => {
		posthog?.capture(
			"user_clicks_no_store_cta_v1",
			{
				page: "save_store",
				newUser,
			},
			{ transport: "sendBeacon" },
		)
	}

	useEffect(() => {
		const hasOtherStoresLinked = Object.keys(state.stores).length > 0

		if (window?.dataLayer && !hasOtherStoresLinked) {
			window.dataLayer.push({ event: "sign_up_kopy" })
		}
		if (window?.fbq && !hasOtherStoresLinked) {
			window.fbq("track", "CompleteRegistration")
		}

		const migrationQueryParamIsPresent = searchParams.has("migration")
		if (
			migrationQueryParamIsPresent &&
			localStorage.getItem("eapp_shop") &&
			localStorage.getItem("eapp_handoff") &&
			!state.stores[localStorage.getItem("eapp_shop")] &&
			!state.savedStores.includes([localStorage.getItem("eapp_shop")])
		) {
			_saveStore(localStorage.getItem("eapp_shop"))
		}
	}, [])

	const hasAStoreWithTokenSelected = state.shop && state.accessToken // User has a linked store selected and clicked on "Add another store"
	const hasAnotherStoreLinked = Object.keys(state.stores).length > 0
	const onlyHasASavedStore =
		!hasAnotherStoreLinked && state.savedStores.includes(state.shop) // User has a saved store but hasn't completed the onboarding and wants to add another store instead

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "0rem 1rem",
			}}
		>
			<TopMenu
				goBack={
					hasAStoreWithTokenSelected || onlyHasASavedStore ? "/" : ""
				}
				accountSelector
				storeSelector={hasAnotherStoreLinked}
			/>

			<div
				style={{
					display: "flex",
					gap: "1rem",
					justifyContent: "center",
					position: "relative",
					width: "100%",
				}}
			>
				<div
					className="border shadow"
					ref={containerRef}
					style={{
						marginTop: "5rem",
						marginBottom: "5rem",
						maxWidth: isMobile ? "100%" : "450px",
						padding: "1rem",
						background: "#fff",
						borderRadius: "2rem",
						flex: "1",
					}}
				>
					<img
						style={{
							display: "block",
							height: "15rem",
							width: "100%",
							objectFit: "cover",
							margin: "auto",
						}}
						src="https://kopy.b-cdn.net/notepad1.webp"
						srcSet="https://kopy.b-cdn.net/notepad2.webp 2x"
					/>

					<div
						style={{
							marginTop: "1rem",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: "0.5rem",
						}}
					>
						<h1
							style={{
								fontSize: "2.5rem",
								fontWeight: "700",
								color: "#222",
								lineHeight: "1",
							}}
						>
							{t("save_store.get_started")}
						</h1>
						<p
							style={{
								fontSize: "1rem",
								fontWeight: "500",
								color: "#777",
								lineHeight: "1",
							}}
						>
							{hasAnotherStoreLinked
								? t("save_store.add_another_store")
								: t("save_store.add_your_first_store")}
						</p>
					</div>

					<div
						style={{
							marginTop: "3rem",
							display: "flex",
							justifyContent: "center",
						}}
					>
						<div style={{ width: "20rem" }}>
							<Tooltip
								content={
									<div style={{ padding: "0.5rem 0.7rem" }}>
										<div style={{ marginBottom: "0.5rem" }}>
											{t(
												"save_store.you_can_use_any_of_these_domains_to_connect_your_store",
											)}
										</div>
										<div style={{ fontWeight: "600" }}>
											<div>
												https://store.myshopify.com
											</div>
											<div>
												https://admin.shopify.com/store/.......
											</div>
											<div>
												https://yourstoredomain.com
											</div>
										</div>
									</div>
								}
							>
								<div
									style={{
										fontSize: "0.9rem",
										fontWeight: "600",
										color: "#222",
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
									}}
								>
									<div
										style={{ height: "15px", fill: "#666" }}
									>
										<Icon source={CircleInformationMajor} />
									</div>
									<span className="info-underline">
										{t(
											"save_store.add_your_shopify_store_domain",
										)}
									</span>
								</div>
							</Tooltip>
							<div
								style={{
									position: "relative",
									marginTop: "0.2rem",
								}}
							>
								<svg
									style={{
										position: "absolute",
										top: "0.4rem",
										left: "0.4rem",
										color: "#777",
									}}
									width="1.4rem"
									height="1.4rem"
									viewBox="0 0 24 24"
								>
									<path
										fill="currentColor"
										d="M16.36 14c.08-.66.14-1.32.14-2c0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2m-5.15 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56M14.34 14H9.66c-.1-.66-.16-1.32-.16-2c0-.68.06-1.35.16-2h4.68c.09.65.16 1.32.16 2c0 .68-.07 1.34-.16 2M12 19.96c-.83-1.2-1.5-2.53-1.91-3.96h3.82c-.41 1.43-1.08 2.76-1.91 3.96M8 8H5.08A7.923 7.923 0 0 1 9.4 4.44C8.8 5.55 8.35 6.75 8 8m-2.92 8H8c.35 1.25.8 2.45 1.4 3.56A8.008 8.008 0 0 1 5.08 16m-.82-2C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2c0 .68.06 1.34.14 2M12 4.03c.83 1.2 1.5 2.54 1.91 3.97h-3.82c.41-1.43 1.08-2.77 1.91-3.97M18.92 8h-2.95a15.65 15.65 0 0 0-1.38-3.56c1.84.63 3.37 1.9 4.33 3.56M12 2C6.47 2 2 6.5 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2Z"
									></path>
								</svg>
								<input
									name="myshopify.com"
									placeholder="https://admin.shopify.com/store/..."
									autoComplete="on"
									onChange={changeStore}
									value={shopDomain}
									className="border shadow"
									style={{
										borderRadius: "1rem",
										padding: "0.5rem",
										paddingLeft: "2rem",
										fontSize: "0.9rem",
										width: "100%",
									}}
								/>
							</div>
						</div>
					</div>

					<div
						style={{
							marginTop: "1rem",
							display: "flex",
							justifyContent: "center",
						}}
					>
						<div
							style={{
								display: "flex",
								gap: "0.3rem",
								alignItems: "center",
								padding: "0.4rem 0.7rem",
								background: "#eee",
								borderRadius: "2rem",
							}}
							onClick={() => setOpenedShowMore((prev) => !prev)}
						>
							<svg
								width="1rem"
								height="1rem"
								viewBox="0 0 512 512"
							>
								<path
									d="M256 32C132.3 32 32 132.3 32 256s100.3 224 224 224 224-100.3 224-224S379.7 32 256 32zm20.2 326.7c-.5 17.8-13.7 28.8-30.8 28.3-16.4-.5-29.3-12.2-28.8-30.1.5-17.8 14.1-29.1 30.5-28.6 17.2.5 29.7 12.6 29.1 30.4zm48.7-127.3c-4.2 5.9-13.6 13.5-25.4 22.7l-13.1 9c-6.4 4.9-10.4 10.7-12.5 17.3-1.1 3.5-1.9 12.6-2.1 18.7-.1 1.2-.8 3.9-4.5 3.9h-39.1c-4.1 0-4.6-2.4-4.5-3.6.6-16.6 3-30.3 9.9-41.3 9.3-14.8 35.5-30.4 35.5-30.4 4-3 7.1-6.2 9.5-9.7 4.4-6 8-12.7 8-19.9 0-8.3-2-16.2-7.3-22.8-6.2-7.7-12.9-11.4-25.8-11.4-12.7 0-20.1 6.4-25.4 14.8-5.3 8.4-4.4 18.3-4.4 27.3H175c0-34 8.9-55.7 27.7-68.5 12.7-8.7 28.9-12.5 47.8-12.5 24.8 0 44.5 4.6 61.9 17.8 16.1 12.2 24.6 29.4 24.6 52.6 0 14.3-5 26.3-12.1 36z"
									fill="currentColor"
								></path>
							</svg>
							<span
								style={{
									fontSize: "0.85rem",
									fontWeight: "700",
									color: "#333",
									textDecoration: "underline",
									cursor: "pointer",
								}}
							>
								{t("save_store.where_do_i_obtain_the_domain")}
							</span>
						</div>
					</div>

					<Collapse in={openedShowMore} transitionDuration={500}>
						<div
							style={{
								marginTop: "1rem",
								display: "flex",
								flexDirection: "column",
								gap: "0.5rem",
								padding: "1rem",
							}}
						>
							<p style={{ fontSize: "0.9rem" }}>
								<b>1.</b>{" "}
								<a
									href="https://admin.shopify.com"
									target="_blank"
									rel="noopener noreferrer"
									style={{
										display: "inline-flex",
										gap: "0.2rem",
										alignItems: "center",
										padding: "0.1rem 0.4rem",
										background: "#fff",
										borderRadius: "1rem",
										border: "1px solid #ccc",
										boxShadow:
											"0 1px 1px rgba(0,0,0,0.1), 0 2px 2px rgba(0,0,0,0.1)",
										cursor: "pointer",
										textDecoration: "none",
										color: "#222",
									}}
								>
									<span>{t("save_store.domain_step_1")}</span>
									<svg
										style={{
											height: "1rem",
											width: "1rem",
											color: "#222",
										}}
										viewBox="0 0 16 16"
									>
										<g fill="currentColor">
											<path d="M6.22 8.72a.75.75 0 0 0 1.06 1.06l5.22-5.22v1.69a.75.75 0 0 0 1.5 0v-3.5a.75.75 0 0 0-.75-.75h-3.5a.75.75 0 0 0 0 1.5h1.69z"></path>
											<path d="M3.5 6.75c0-.69.56-1.25 1.25-1.25H7A.75.75 0 0 0 7 4H4.75A2.75 2.75 0 0 0 2 6.75v4.5A2.75 2.75 0 0 0 4.75 14h4.5A2.75 2.75 0 0 0 12 11.25V9a.75.75 0 0 0-1.5 0v2.25c0 .69-.56 1.25-1.25 1.25h-4.5c-.69 0-1.25-.56-1.25-1.25z"></path>
										</g>
									</svg>
								</a>
							</p>
							<p style={{ fontSize: "0.9rem" }}>
								<b>2.</b> {t("save_store.domain_step_2")}
							</p>
							<Video videoSrc={CopyShopifyStoreUrlVideo} />
							<p style={{ fontSize: "0.9rem" }}>
								<b>3.</b> {t("save_store.domain_step_3")}
							</p>
						</div>
					</Collapse>

					{errorCode ? (
						<div style={{ marginTop: "1rem" }}>
							<Alert
								title={t("save_store.error_adding_store")}
								color="red"
								radius="lg"
								variant="filled"
							>
								{errorCode === "no_userid_field" ? (
									<div>No userId found</div>
								) : errorCode === "invalid_domain" ||
								  errorCode === "no_store_field" ? (
									<div style={{ fontSize: "0.9rem" }}>
										<div style={{ marginBottom: "0.5rem" }}>
											{t(
												"save_store.the_domain_you_entered_is_not_a_myshopify_com_domain",
											)}
										</div>
										<div style={{ marginBottom: "0.5rem" }}>
											{t(
												"save_store.it_should_look_something_like",
											)}
										</div>
										<div
											style={{
												marginBottom: "0.5rem",
												fontWeight: "600",
											}}
										>
											<div>
												https://store.myshopify.com
											</div>
											<div>
												https://admin.shopify.com/store/...
											</div>
										</div>
										<div>
											{t(
												"save_store.custom_domain_alternative",
											)}
										</div>
									</div>
								) : errorCode ===
								  "already_linked_to_same_account" ? (
									<div>
										This store is already linked to this
										account
									</div>
								) : errorCode ===
								  "already_linked_to_another_account" ? (
									<div>
										This store is already linked to another
										account ({censoredEmail})
									</div>
								) : (
									<div>Unexpected error</div>
								)}
							</Alert>
						</div>
					) : (
						""
					)}

					<div
						style={{
							marginTop: "4rem",
							display: "flex",
							justifyContent: "space-between",
						}}
					>
						<Button
							external
							url="https://autostore.ai/?utm_source=kopy&utm_medium=onboarding&utm_campaign=save_store"
							onClick={handleNoStoreClick}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									color: "#222",
								}}
							>
								<span
									style={{
										fontSize: "0.9rem",
										fontWeight: "600",
									}}
								>
									{t("save_store.no_store_cta")}
								</span>
							</div>
						</Button>
						<Button
							onClick={() => _saveStore(shopDomain)}
							disabled={loading}
						>
							{loading ? (
								<Spinner />
							) : (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.3rem",
										color: "#222",
									}}
								>
									<span
										style={{
											fontSize: "0.9rem",
											fontWeight: "600",
										}}
									>
										{t("common.next")}
									</span>
									<svg
										width="1rem"
										height="1rem"
										viewBox="0 0 512 512"
									>
										<path
											d="M284.9 412.6l138.1-134c6-5.8 9-13.7 9-22.4v-.4c0-8.7-3-16.6-9-22.4l-138.1-134c-12-12.5-31.3-12.5-43.2 0-11.9 12.5-11.9 32.7 0 45.2l83 79.4h-214c-17 0-30.7 14.3-30.7 32 0 18 13.7 32 30.6 32h214l-83 79.4c-11.9 12.5-11.9 32.7 0 45.2 12 12.5 31.3 12.5 43.3 0z"
											fill="currentColor"
										></path>
									</svg>

									{/* Previous arrow icon */}
									{/* <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 512 512"><path d="M401.4 224h-214l83-79.4c11.9-12.5 11.9-32.7 0-45.2s-31.2-12.5-43.2 0L89 233.4c-6 5.8-9 13.7-9 22.4v.4c0 8.7 3 16.6 9 22.4l138.1 134c12 12.5 31.3 12.5 43.2 0 11.9-12.5 11.9-32.7 0-45.2l-83-79.4h214c16.9 0 30.6-14.3 30.6-32 .1-18-13.6-32-30.5-32z" fill="currentColor"></path></svg> */}
								</div>
							)}
						</Button>
					</div>
				</div>

				<div
					className="float-right"
					style={{
						left: `calc(${containerRight}px + 0.2rem)`,
						top: "7rem",
						paddingRight: "1rem",
					}}
				>
					<OnboardingTimeline
						step={newUser ? 2 : 1}
						newUser={newUser}
					/>
					<style>{`
						.float-right {
							position: absolute;
							width: max-content;
						}

						@media (max-width: 900px) {
							.float-right {
								display: none;
							}
						}
						@media (max-width: 1200px) {
							.float-right {
								width: fit-content;
							}
						}
					`}</style>
				</div>
			</div>

			<div
				style={{
					position: "fixed",
					bottom: "2rem",
					left: "2rem",
					zIndex: "500",
				}}
			>
				<LanguageSelector />
			</div>
		</div>
	)
}

export default SaveStore
